import { useEffect, useMemo, useState } from "react";
import { Input, Modal, Table, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  BankOutlined,
  CheckCircleFilled,
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ShopOutlined,
  StarFilled,
  StarOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useViewport } from "../../../../hooks/useViewport";
import { modalStyles } from "../../../../utils/modalStyles";
import type {
  Company,
  CompanyBankInput,
  CompanyPayload,
} from "../../../../api/companies";
import {
  isValidBIC,
  isValidBgVat,
  isValidEIK,
  isValidEmail,
  isValidIBAN,
  isValidProforma,
  normalizeVatInput,
} from "../../../../lib/companyValidators";
import { Field, Row, Section, ToggleRow } from "./companyFormFields";
import { NumberingSection } from "./NumberingSection";
import { useUnsavedChangesGuard } from "../panel/useUnsavedChangesGuard";

const R = {
  bg: "var(--color-canvas)",
  surface: "var(--color-surface)",
  border: "var(--color-shade-2)",
  textSoft: "var(--color-body)",
  textMuted: "var(--color-muted)",
  ink: "var(--color-ink)",
  // Плътен акцентен фон (save бутон, switch) — еднакъв в двете теми.
  brand: "#D97706",
  brandDeep: "var(--color-brand-deep-ink)",
  brandTint: "var(--color-brand-tint)",
  danger: "var(--color-danger-ink)",
};

interface Props {
  open: boolean;
  mode: "create" | "edit";
  company?: Company | null;
  onSubmit: (data: CompanyPayload) => Promise<void>;
  onCancel: () => void;
}

type BankRow = CompanyBankInput & { _localId: number };
type BankDraft = CompanyBankInput & { editingLocalId: number | null };

const emptyDraft = (): BankDraft => ({
  editingLocalId: null,
  bank_name: "",
  bic: "",
  iban: "",
  is_default: false,
});

const CompanyModal = ({ open, mode, company, onSubmit, onCancel }: Props) => {
  const { t } = useTranslation();
  const { isMobile, isNarrow, isTablet } = useViewport();

  const isEdit = mode === "edit";
  // На narrow стои 1 колона; на широки — 2 колони bento grid.
  // Сваляме layout-а до 1 колона още на таблет — двуколонният вариант
  // притиска „Банкови сметки" таблицата и прави IBAN-а нечетим.
  const isSingleColumn = isNarrow || isTablet;

  // Полета
  const [name, setName] = useState("");
  const [objectName, setObjectName] = useState("");
  const [address, setAddress] = useState("");
  const [mol, setMol] = useState("");
  const [eik, setEik] = useState("");
  const [vatNumber, setVatNumber] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  // separate_invoice_numbering е винаги true след премахването на toggle-а
  // от UI-а — оставяме като константа, за да не пипаме всички референции.
  const separateNumbering = true;
  // При редакция се попълва записаната стойност (решение от 19.08.2026,
  // обръща „винаги празно" от 0.0.186) — операторът вижда докъде е стигнала
  // номерацията и я пипа само ако трябва. При създаване стартира празно.
  const [lastInvoiceNumber, setLastInvoiceNumber] = useState<number | null>(
    null,
  );
  const [lastProformaNumber, setLastProformaNumber] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const [banks, setBanks] = useState<BankRow[]>([]);
  const [draft, setDraft] = useState<BankDraft | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Текстовете на грешките се показват чак след неуспешен опит за запис —
  // дотогава задължителните полета се познават само по звездичката.
  const [showErrors, setShowErrors] = useState(false);

  // Пазач за незапазени промени — пита при затваряне, ако нещо е пипано
  // (включително започнатата, още недобавена банкова сметка).
  const { requestClose, markPristine, unsavedDialog } = useUnsavedChangesGuard({
    open,
    snapshot: JSON.stringify({
      name,
      objectName,
      address,
      mol,
      eik,
      vatNumber,
      phone,
      email,
      lastInvoiceNumber,
      lastProformaNumber,
      isDefault,
      banks: banks.map((bank) => [bank.bank_name, bank.bic, bank.iban, bank.is_default]),
      draft,
    }),
    isSubmitting,
    onClose: onCancel,
  });

  // Валиден ЕИК се отбелязва веднага, а ДДС номерът може да се попълни от него
  // с един клик — само докато полето е празно, за да не трие ръчно въведено.
  const eikIsValid = isValidEIK(eik);
  const canFillVatFromEik = eikIsValid && !vatNumber.trim();

  useEffect(() => {
    if (!open) return;
    if (isEdit && company) {
      setName(company.name);
      setObjectName(company.object_name);
      setAddress(company.address ?? "");
      setMol(company.mol ?? "");
      setEik(company.eik);
      setVatNumber(company.vat_number ?? "");
      setPhone(company.phone ?? "");
      setEmail(company.email ?? "");
      setLastInvoiceNumber(company.last_invoice_number ?? null);
      setLastProformaNumber(company.last_proforma_number ?? "");
      setIsDefault(company.is_default);
      setBanks(
        company.banks.map((b, i) => ({
          _localId: i,
          bank_name: b.bank_name,
          bic: b.bic,
          iban: b.iban,
          is_default: b.is_default,
        })),
      );
    } else {
      setName("");
      setObjectName("");
      setAddress("");
      setMol("");
      setEik("");
      setVatNumber("");
      setPhone("");
      setEmail("");
      setLastInvoiceNumber(null);
      setLastProformaNumber("");
      setIsDefault(false);
      setBanks([]);
    }
    setDraft(null);
    setShowErrors(false);
    markPristine();
  }, [open, isEdit, company, markPristine]);

  // ── Top-level field validation ─────────────────────────────────────────────
  const errors = useMemo(() => {
    const e: Record<string, string> = {};
    if (!name.trim()) e.name = t("companiesConfig.errRequired");
    if (!objectName.trim()) e.object_name = t("companiesConfig.errRequired");
    if (!eik.trim()) e.eik = t("companiesConfig.errRequired");
    else if (!isValidEIK(eik.trim()))
      e.eik = t("companiesConfig.errInvalidEik");
    if (vatNumber.trim() && !isValidBgVat(vatNumber))
      e.vat_number = t("companiesConfig.errInvalidVat");
    if (email.trim() && !isValidEmail(email))
      e.email = t("companiesConfig.errInvalidEmail");
    if (separateNumbering) {
      if (lastInvoiceNumber === null) {
        e.last_invoice_number = t("companiesConfig.errRequired");
      } else if (lastInvoiceNumber < 0 || lastInvoiceNumber > 9_999_999_999) {
        e.last_invoice_number = t("companiesConfig.errInvalidInvoiceNumber");
      }
      if (
        lastProformaNumber.trim() &&
        !isValidProforma(lastProformaNumber.trim())
      ) {
        e.last_proforma_number = t("companiesConfig.errInvalidProforma");
      }
    }
    return e;
  }, [
    name,
    objectName,
    eik,
    vatNumber,
    email,
    separateNumbering,
    lastInvoiceNumber,
    lastProformaNumber,
    t,
  ]);

  // ── Bank draft validation ──────────────────────────────────────────────────
  const draftErrors = useMemo(() => {
    if (!draft) return {} as Record<string, string>;
    const e: Record<string, string> = {};
    if (!draft.bank_name.trim())
      e.bank_name = t("companiesConfig.errRequired");
    if (!isValidBIC(draft.bic.trim()))
      e.bic = t("companiesConfig.errInvalidBic");
    if (!isValidIBAN(draft.iban.trim()))
      e.iban = t("companiesConfig.errInvalidIban");
    return e;
  }, [draft, t]);

  const canSaveDraft = draft !== null && Object.keys(draftErrors).length === 0;
  const draftDirty =
    draft !== null &&
    (draft.bank_name.trim() !== "" ||
      draft.bic.trim() !== "" ||
      draft.iban.trim() !== "");

  // Прозоречно горно ниво: не позволяваме save докато имаме незавършена
  // банка с поне едно попълнено поле, за да не се изгубят данни. Грешките
  // във формата НЕ гасят бутона — натискането му ги показва (showErrors).
  const canSave = !draftDirty && !isSubmitting;
  // В UI-а грешките излизат чак след опит за запис.
  const visibleErrors: typeof errors = showErrors ? errors : {};

  const commitDraft = () => {
    if (!draft || !canSaveDraft) return;
    // При нова банка: ако още няма default между записаните — новата става default.
    const becomesFirstDefault =
      draft.editingLocalId === null && banks.every((b) => !b.is_default);
    const normalized: BankRow = {
      _localId:
        draft.editingLocalId ?? (banks.length ? Math.max(...banks.map((b) => b._localId)) + 1 : 1),
      bank_name: draft.bank_name.trim(),
      bic: draft.bic.replace(/\s+/g, "").toUpperCase(),
      iban: draft.iban.replace(/\s+/g, "").toUpperCase(),
      is_default: draft.is_default || becomesFirstDefault,
    };
    if (draft.editingLocalId !== null) {
      setBanks((rows) =>
        rows.map((r) => (r._localId === draft.editingLocalId ? normalized : r)),
      );
    } else {
      setBanks((rows) => [...rows, normalized]);
    }
    setDraft(null);
  };

  // Маркира една сметка като default, изчиства флага от всички останали.
  const setDefaultBank = (id: number) => {
    setBanks((rows) =>
      rows.map((r) => ({ ...r, is_default: r._localId === id })),
    );
  };

  const openDraftForAdd = () => {
    // 5 натискания на бутона не отварят 5 форми — ако вече има drag-form,
    // оставяме я отворена.
    if (draft !== null) return;
    setDraft(emptyDraft());
  };

  const openDraftForEdit = (row: BankRow) => {
    setDraft({
      editingLocalId: row._localId,
      bank_name: row.bank_name,
      bic: row.bic,
      iban: row.iban,
      is_default: row.is_default,
    });
  };

  const cancelDraft = () => setDraft(null);

  const removeBank = (id: number) => {
    setBanks((rows) => {
      const filtered = rows.filter((r) => r._localId !== id);
      const removedDefault = rows.find((r) => r._localId === id)?.is_default;
      // Ако сме изтрили default-ната и са останали други — промотираме първата.
      if (removedDefault && filtered.length && filtered.every((r) => !r.is_default)) {
        filtered[0] = { ...filtered[0], is_default: true };
      }
      return filtered;
    });
    if (draft?.editingLocalId === id) setDraft(null);
  };

  // ── Save ───────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!canSave) return;
    if (Object.keys(errors).length > 0) {
      setShowErrors(true);
      return;
    }
    setIsSubmitting(true);
    try {
      const payload: CompanyPayload = {
        name: name.trim(),
        object_name: objectName.trim(),
        address: address.trim() || null,
        mol: mol.trim() || null,
        eik: eik.trim(),
        vat_number: vatNumber.trim()
          ? vatNumber.replace(/\s+/g, "").toUpperCase()
          : null,
        phone: phone.trim() || null,
        email: email.trim() || null,
        separate_invoice_numbering: separateNumbering,
        last_invoice_number: separateNumbering ? lastInvoiceNumber ?? 0 : 0,
        last_proforma_number:
          separateNumbering && lastProformaNumber.trim()
            ? lastProformaNumber.trim().toUpperCase()
            : null,
        is_default: isDefault,
        banks: banks.map((b) => ({
          bank_name: b.bank_name,
          bic: b.bic,
          iban: b.iban,
          is_default: b.is_default,
        })),
      };
      await onSubmit(payload);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Banks table columns ────────────────────────────────────────────────────
  const bankColumns: ColumnsType<BankRow> = [
    {
      title: "",
      key: "default",
      width: 40,
      align: "center",
      render: (_: unknown, row: BankRow) => (
        <Tooltip title={t("companiesConfig.bankMakeDefault")}>
          <button
            type="button"
            onClick={() => setDefaultBank(row._localId)}
            aria-label={t("companiesConfig.bankMakeDefault")}
            style={{
              height: 26,
              width: 26,
              borderRadius: 6,
              border: "none",
              background: "transparent",
              color: row.is_default ? "var(--color-brand-ink)" : R.textMuted,
              cursor: row.is_default ? "default" : "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "color 120ms",
            }}
          >
            {row.is_default ? (
              <StarFilled style={{ fontSize: 14 }} />
            ) : (
              <StarOutlined style={{ fontSize: 14 }} />
            )}
          </button>
        </Tooltip>
      ),
    },
    {
      title: t("companiesConfig.bankName"),
      dataIndex: "bank_name",
      key: "bank_name",
      render: (v: string) => (
        <span style={{ fontSize: 12.5, color: R.ink, fontWeight: 600 }}>
          {v}
        </span>
      ),
    },
    {
      title: "BIC",
      dataIndex: "bic",
      key: "bic",
      width: 110,
      render: (v: string) => (
        <span
          style={{
            fontFamily:
              '"JetBrains Mono", ui-monospace, SFMono-Regular, monospace',
            fontSize: 12,
            color: R.textSoft,
          }}
        >
          {v}
        </span>
      ),
    },
    {
      title: "IBAN",
      dataIndex: "iban",
      key: "iban",
      // BG IBAN = 22 символа. На един ред е по-четимо от wrap, така че
      // задаваме фиксирана ширина + nowrap; при тесен контейнер таблицата
      // ще скролне хоризонтално (scroll x по-долу).
      width: 240,
      render: (v: string) => (
        <span
          style={{
            fontFamily:
              '"JetBrains Mono", ui-monospace, SFMono-Regular, monospace',
            fontSize: 12,
            color: R.textSoft,
            whiteSpace: "nowrap",
            letterSpacing: 0.2,
          }}
        >
          {(v ?? "").replace(/\s+/g, "").replace(/(.{4})/g, "$1 ").trim()}
        </span>
      ),
    },
    {
      key: "actions",
      width: 84,
      align: "right",
      render: (_: unknown, row: BankRow) => (
        <div style={{ display: "inline-flex", gap: 6 }}>
          <MiniBtn
            onClick={() => openDraftForEdit(row)}
            label={t("common.edit", "Edit")}
          >
            <EditOutlined />
          </MiniBtn>
          <MiniBtn
            onClick={() => removeBank(row._localId)}
            label={t("common.delete", "Delete")}
            danger
          >
            <DeleteOutlined />
          </MiniBtn>
        </div>
      ),
    },
  ];

  const modalWidth = isMobile ? undefined : isTablet ? 820 : 1120;

  return (
    <Modal
      open={open}
      onCancel={requestClose}
      // Esc го поема пазачът за незапазени промени (useUnsavedChangesGuard).
      keyboard={false}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={modalWidth}
      styles={modalStyles}
      style={{ maxWidth: "calc(100vw - 32px)" }}
    >
      {unsavedDialog}
      <style>{`
        @keyframes rspin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
        .co-modal .ant-input,
        .co-modal .ant-input-number {
          border-radius: 10px !important;
          border-color: ${R.border} !important;
          background: ${R.surface} !important;
          font-size: 13px;
          color: ${R.ink};
          transition: border-color 120ms, box-shadow 120ms;
        }
        .co-modal .ant-input { min-height: 40px !important; height: 40px !important; padding: 4px 12px !important; }
        .co-modal .ant-input-number { width: 100% !important; }
        .co-modal .ant-input-number .ant-input-number-input {
          height: 38px !important;
          padding: 4px 12px !important;
          font-size: 13px;
        }
        .co-modal .ant-input:hover,
        .co-modal .ant-input:focus,
        .co-modal .ant-input-number:hover,
        .co-modal .ant-input-number-focused {
          border-color: ${R.brand} !important;
          box-shadow: 0 0 0 3px rgba(217,119,6,0.12) !important;
        }
        .co-modal .ant-input::placeholder { color: ${R.textMuted} !important; }
        .co-modal .co-err input,
        .co-modal .co-err .ant-input,
        .co-modal .co-err .ant-input-number {
          border-color: var(--color-danger) !important;
          box-shadow: 0 0 0 3px rgba(220,38,38,0.12) !important;
        }
        .co-modal .ant-switch-checked { background: ${R.brand} !important; }
        .co-modal .bank-table .ant-table { background: transparent; }
        .co-modal .bank-table .ant-table-thead > tr > th {
          background: ${R.bg} !important;
          color: ${R.textMuted} !important;
          font-size: 10px !important;
          font-weight: 700 !important;
          letter-spacing: 0.8px !important;
          text-transform: uppercase !important;
          padding: 8px 12px !important;
          border-bottom: 1px solid ${R.border} !important;
        }
        .co-modal .bank-table .ant-table-thead > tr > th::before { display: none !important; }
        .co-modal .bank-table .ant-table-tbody > tr > td {
          padding: 9px 12px !important;
          border-bottom: 1px solid ${R.border} !important;
          font-size: 12.5px;
        }
        .co-modal .bank-table .ant-table-tbody > tr:last-child > td { border-bottom: none !important; }
      `}</style>

      <div
        style={{
          background: R.surface,
          display: "flex",
          flexDirection: "column",
          maxHeight: "90vh",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: isNarrow ? "14px 16px" : "20px 24px",
            borderBottom: `1px solid ${R.border}`,
            display: "flex",
            alignItems: "flex-start",
            gap: 12,
            flexShrink: 0,
          }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              background: R.brandTint,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: R.brandDeep,
              flexShrink: 0,
            }}
          >
            {isEdit ? (
              <EditOutlined style={{ fontSize: 18 }} />
            ) : (
              <ShopOutlined style={{ fontSize: 18 }} />
            )}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: isNarrow ? 16 : 17,
                fontWeight: 700,
                letterSpacing: -0.3,
                color: R.ink,
              }}
            >
              {isEdit
                ? t("companiesConfig.editTitle")
                : t("companiesConfig.createTitle")}
            </div>
            <div
              style={{
                fontSize: 13,
                fontWeight: 500,
                color: R.textSoft,
                marginTop: 4,
              }}
            >
              {isEdit
                ? t("companiesConfig.editSubtitle")
                : t("companiesConfig.createSubtitle")}
            </div>
          </div>
          <button
            onClick={requestClose}
            aria-label="Close"
            style={{
              height: 30,
              width: 30,
              borderRadius: 6,
              background: "transparent",
              border: "none",
              color: R.textMuted,
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            <CloseOutlined style={{ fontSize: 14 }} />
          </button>
        </div>

        {/* Body — 2-col bento grid (1-col на narrow) */}
        <div
          className="co-modal"
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            padding: isNarrow ? "14px 16px" : "18px 22px",
            background: R.bg,
            display: "grid",
            gridTemplateColumns: isSingleColumn ? "1fr" : "1fr 1fr",
            gap: 14,
            alignItems: "start",
          }}
        >
          {/* ── Колона 1 ── */}
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <Section title={t("companiesConfig.secBasic")}>
              <Field
                label={t("companiesConfig.fieldName")}
                error={visibleErrors.name}
                required
              >
                <Input
                  className={visibleErrors.name ? "co-err" : ""}
                  value={name}
                  maxLength={255}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t("companiesConfig.fieldNamePlaceholder")}
                />
              </Field>
              <Field
                label={t("companiesConfig.fieldObjectName")}
                error={visibleErrors.object_name}
                required
              >
                <Input
                  className={visibleErrors.object_name ? "co-err" : ""}
                  value={objectName}
                  maxLength={255}
                  onChange={(e) => setObjectName(e.target.value)}
                  placeholder={t(
                    "companiesConfig.fieldObjectNamePlaceholder",
                  )}
                />
              </Field>
              <Row narrow={isNarrow}>
                <Field label={t("companiesConfig.fieldAddress")}>
                  <Input
                    value={address}
                    maxLength={500}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder={t("companiesConfig.fieldAddressPlaceholder")}
                  />
                </Field>
                <Field label={t("companiesConfig.fieldMol")}>
                  <Input
                    value={mol}
                    maxLength={255}
                    onChange={(e) => setMol(e.target.value)}
                    placeholder={t("companiesConfig.fieldMolPlaceholder")}
                  />
                </Field>
              </Row>
            </Section>

            <Section title={t("companiesConfig.secStatus")}>
              <ToggleRow
                checked={isDefault}
                onChange={setIsDefault}
                title={t("companiesConfig.fieldDefault")}
                desc={t("companiesConfig.fieldDefaultDesc")}
              />
            </Section>

            <NumberingSection
              lastInvoiceNumber={lastInvoiceNumber}
              onLastInvoiceNumberChange={setLastInvoiceNumber}
              lastProformaNumber={lastProformaNumber}
              onLastProformaNumberChange={setLastProformaNumber}
              invoiceError={visibleErrors.last_invoice_number}
              proformaError={visibleErrors.last_proforma_number}
              isNarrow={isNarrow}
            />
          </div>

          {/* ── Колона 2 ── */}
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <Section title={t("companiesConfig.secIdentity")}>
              <Row narrow={isNarrow}>
                <Field
                  label={t("companiesConfig.fieldEik")}
                  error={visibleErrors.eik}
                  required
                  hint={t("companiesConfig.fieldEikHint")}
                >
                  <Input
                    className={visibleErrors.eik ? "co-err" : ""}
                    value={eik}
                    maxLength={13}
                    inputMode="numeric"
                    onChange={(e) =>
                      setEik(e.target.value.replace(/\D/g, "").slice(0, 13))
                    }
                    placeholder="123456789"
                    // Тиха отметка, щом контролната сума излезе. Дотук полето
                    // мълчеше, докато не гръмне при запис, и валиден ЕИК не се
                    // различаваше от сгрешен, преди да се натисне „Запази“.
                    suffix={
                      eikIsValid ? (
                        <Tooltip title={t("companiesConfig.fieldEikValid")}>
                          <CheckCircleFilled
                            aria-label={t("companiesConfig.fieldEikValid")}
                            className="text-[13px] text-ok-deep"
                          />
                        </Tooltip>
                      ) : (
                        <span />
                      )
                    }
                  />
                </Field>
                <Field
                  label={t("companiesConfig.fieldVat")}
                  error={visibleErrors.vat_number}
                  hint={
                    canFillVatFromEik ? (
                      <button
                        type="button"
                        onClick={() => setVatNumber(`BG${eik}`)}
                        className="text-[11px] font-semibold text-brand-deep-ink underline underline-offset-2 transition-colors hover:text-brand-ink"
                      >
                        {t("companiesConfig.fieldVatFromEik", { eik })}
                      </button>
                    ) : (
                      t("companiesConfig.fieldVatHint")
                    )
                  }
                >
                  <Input
                    className={visibleErrors.vat_number ? "co-err" : ""}
                    value={vatNumber}
                    maxLength={20}
                    onChange={(e) => setVatNumber(normalizeVatInput(e.target.value))}
                    placeholder="BG123456789"
                  />
                </Field>
              </Row>
              <Row narrow={isNarrow}>
                <Field label={t("companiesConfig.fieldPhone")}>
                  <Input
                    value={phone}
                    maxLength={50}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+359 ..."
                  />
                </Field>
                <Field
                  label={t("companiesConfig.fieldEmail")}
                  error={visibleErrors.email}
                >
                  <Input
                    className={visibleErrors.email ? "co-err" : ""}
                    value={email}
                    maxLength={255}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="office@example.com"
                  />
                </Field>
              </Row>
            </Section>

            <Section
              title={t("companiesConfig.secBanks")}
              action={
                <button
                  type="button"
                  onClick={openDraftForAdd}
                  disabled={draft !== null}
                  style={{
                    height: 32,
                    padding: "0 12px",
                    borderRadius: 8,
                    border: `1px solid ${R.border}`,
                    background: draft !== null ? R.bg : R.surface,
                    color: draft !== null ? R.textMuted : R.brandDeep,
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: draft !== null ? "not-allowed" : "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <PlusOutlined style={{ fontSize: 11 }} />
                  {t("companiesConfig.addBank")}
                </button>
              }
            >
              {banks.length === 0 && draft === null ? (
                <div
                  style={{
                    padding: 18,
                    border: `1px dashed ${R.border}`,
                    borderRadius: 10,
                    textAlign: "center",
                    color: R.textMuted,
                    fontSize: 12.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                  }}
                >
                  <BankOutlined />
                  {t("companiesConfig.noBanks")}
                </div>
              ) : (
                <>
                  {banks.length > 0 && (
                    <div
                      className="bank-table"
                      style={{
                        background: R.surface,
                        border: `1px solid ${R.border}`,
                        borderRadius: 10,
                        overflow: "hidden",
                      }}
                    >
                      <Table
                        columns={bankColumns}
                        dataSource={banks}
                        rowKey="_localId"
                        pagination={false}
                        size="small"
                        scroll={{ x: 640 }}
                      />
                    </div>
                  )}

                  {draft !== null && (
                    <div
                      style={{
                        background: R.surface,
                        border: `1px solid ${R.border}`,
                        borderRadius: 10,
                        padding: 12,
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <span
                          style={{
                            fontSize: 10.5,
                            fontWeight: 700,
                            color: R.brandDeep,
                            letterSpacing: 1.1,
                            textTransform: "uppercase",
                          }}
                        >
                          {draft.editingLocalId !== null
                            ? t("companiesConfig.bankEditDraft")
                            : t("companiesConfig.bankAddDraft")}
                        </span>
                        <div style={{ display: "inline-flex", gap: 6 }}>
                          <MiniBtn
                            onClick={cancelDraft}
                            label={t("common.cancel")}
                          >
                            <CloseOutlined />
                          </MiniBtn>
                          <MiniBtn
                            onClick={commitDraft}
                            label={t("companiesConfig.bankConfirm")}
                            disabled={!canSaveDraft}
                            primary
                          >
                            <CheckOutlined />
                          </MiniBtn>
                        </div>
                      </div>
                      <Row narrow={isNarrow}>
                        <Field
                          label={t("companiesConfig.bankName")}
                          error={draftErrors.bank_name}
                          required
                        >
                          <Input
                            className={draftErrors.bank_name ? "co-err" : ""}
                            value={draft.bank_name}
                            maxLength={255}
                            onChange={(e) =>
                              setDraft({ ...draft, bank_name: e.target.value })
                            }
                          />
                        </Field>
                        <Field
                          label="BIC"
                          error={draftErrors.bic}
                          required
                        >
                          <Input
                            className={draftErrors.bic ? "co-err" : ""}
                            value={draft.bic}
                            maxLength={11}
                            onChange={(e) =>
                              setDraft({
                                ...draft,
                                bic: e.target.value.toUpperCase(),
                              })
                            }
                            placeholder="UNCRBGSF"
                          />
                        </Field>
                      </Row>
                      <Field
                        label="IBAN"
                        error={draftErrors.iban}
                        required
                      >
                        <Input
                          className={draftErrors.iban ? "co-err" : ""}
                          value={draft.iban}
                          maxLength={34}
                          onChange={(e) =>
                            setDraft({
                              ...draft,
                              iban: e.target.value.toUpperCase(),
                            })
                          }
                          placeholder="BG80BNBG96611020345678"
                        />
                      </Field>
                    </div>
                  )}
                </>
              )}
            </Section>
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            borderTop: `1px solid ${R.border}`,
            padding: isNarrow ? "12px 16px" : "14px 22px",
            display: "flex",
            gap: 10,
            flexShrink: 0,
            background: R.surface,
          }}
        >
          <button
            type="button"
            onClick={requestClose}
            disabled={isSubmitting}
            style={{
              height: 42,
              padding: "0 18px",
              borderRadius: 10,
              border: `1px solid ${R.border}`,
              background: "transparent",
              color: R.textSoft,
              fontSize: 13.5,
              fontWeight: 600,
              cursor: isSubmitting ? "not-allowed" : "pointer",
              flex: 1,
            }}
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            title={
              draftDirty ? t("companiesConfig.draftDirtyBlock") : undefined
            }
            style={{
              height: 42,
              padding: "0 22px",
              borderRadius: 10,
              border: "none",
              background: canSave ? R.brand : "rgba(217,119,6,0.35)",
              color: "#fff",
              fontSize: 13.5,
              fontWeight: 700,
              letterSpacing: -0.2,
              cursor: canSave ? "pointer" : "not-allowed",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              flex: 1,
            }}
          >
            {isSubmitting && (
              <span
                style={{
                  width: 14,
                  height: 14,
                  border: "2px solid rgba(255,255,255,0.5)",
                  borderTopColor: "#fff",
                  borderRadius: "50%",
                  animation: "rspin 900ms linear infinite",
                }}
              />
            )}
            {isEdit
              ? t("companiesConfig.save")
              : t("companiesConfig.create")}
          </button>
        </div>
      </div>
    </Modal>
  );
};

// ── Layout primitives ───────────────────────────────────────────────────────

function MiniBtn({
  children,
  onClick,
  label,
  danger,
  primary,
  disabled,
}: {
  children: React.ReactNode;
  onClick: () => void;
  label: string;
  danger?: boolean;
  primary?: boolean;
  disabled?: boolean;
}) {
  const baseColor = danger
    ? R.danger
    : primary
      ? "#fff"
      : R.textSoft;
  const baseBg = primary
    ? disabled
      ? "rgba(217,119,6,0.35)"
      : R.brand
    : "transparent";
  const baseBorder = primary ? "transparent" : R.border;
  return (
    <Tooltip title={label}>
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        aria-label={label}
        style={{
          height: 28,
          width: 28,
          borderRadius: 7,
          border: `1px solid ${baseBorder}`,
          background: baseBg,
          color: baseColor,
          cursor: disabled ? "not-allowed" : "pointer",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "background 120ms",
        }}
      >
        {children}
      </button>
    </Tooltip>
  );
}

export default CompanyModal;
